import { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Spin,
  Empty,
  Tabs,
  Button,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Popconfirm,
  message,
} from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  useGetStaffMemberQuery,
  useUpdateStaffMemberMutation,
  useDeleteStaffMemberMutation,
  useGetStaffProfilesQuery,
  useGetTasksQuery,
} from "../../../redux/api/scheduling";
import StaffTemplateEditor from "./StaffTemplateEditor";
import StaffCalendar from "./StaffCalendar";

interface Props {
  memberId: number;
  onBack?: () => void;
}

export default function StaffDetail({ memberId, onBack }: Props) {
  const { data: member, isLoading } = useGetStaffMemberQuery(memberId);
  const { data: profiles } = useGetStaffProfilesQuery({});
  const { data: tasksData } = useGetTasksQuery({});
  const [updateMember] = useUpdateStaffMemberMutation();
  const [deleteMember] = useDeleteStaffMemberMutation();
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();

  const profileList: any[] = profiles?.results || profiles || [];
  const taskList: any[] = tasksData?.results || tasksData || [];

  useEffect(() => {
    if (member && editing) {
      form.setFieldsValue({
        name: member.name,
        profile: member.profile,
        tasks: member.tasks || [],
        is_active: member.is_active,
        use_alternating_schedule: member.use_alternating_schedule,
      });
    }
  }, [member, editing, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await updateMember({ id: memberId, ...values }).unwrap();
      message.success("Staff member updated");
      setEditing(false);
    } catch (err: any) {
      if (err?.data) {
        message.error(err.data.detail || JSON.stringify(err.data));
      }
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMember(memberId).unwrap();
      message.success("Staff member deleted");
      onBack?.();
    } catch {
      message.error("Failed to delete staff member");
    }
  };

  if (isLoading) return <Spin size="large" className="mt-10 mx-auto block" />;
  if (!member) return <Empty description="Staff member not found" />;

  return (
    <div>
      <Card
        className="mb-4"
        title={editing ? "Edit Staff Member" : member.name}
        extra={
          editing ? (
            <Space>
              <Button icon={<SaveOutlined />} type="primary" onClick={handleSave}>
                Save
              </Button>
              <Button icon={<CloseOutlined />} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </Space>
          ) : (
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        }
      >
        {editing ? (
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="profile" label="Profile" rules={[{ required: true }]}>
              <Select
                options={profileList.map((p: any) => ({
                  value: p.id,
                  label: `${p.name} (${p.initial})`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="tasks"
              label="Qualified Tasks"
              tooltip="Tasks this staff member can perform."
            >
              <Select
                mode="multiple"
                options={taskList.map((t: any) => ({
                  value: t.id,
                  label: `[${t.task_id}] ${t.name}`,
                }))}
                placeholder="Select tasks..."
                showSearch
                filterOption={(input, option) =>
                  (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <div className="grid grid-cols-2 gap-4">
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item
                name="use_alternating_schedule"
                label="Alternating Schedule"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>
          </Form>
        ) : (
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Profile">
              <Tag>
                {member.profile_initial} – {member.profile_name}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Active">
              {member.is_active ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="red">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Alternating Schedule">
              {member.use_alternating_schedule ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Qualified Tasks" span={2}>
              <Space size={[4, 4]} wrap>
                {(member.tasks_detail || []).map((t: any) => (
                  <Tag key={t.id}>{t.name}</Tag>
                ))}
                {(member.tasks_detail || []).length === 0 && (
                  <span className="text-gray-400 italic text-xs">None assigned</span>
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        )}
        {!editing && (
          <div className="mt-4 pt-3 border-t">
            <Popconfirm
              title="Are you sure you want to delete this staff member?"
              description="This action cannot be undone."
              onConfirm={handleDelete}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete Staff Member
              </Button>
            </Popconfirm>
          </div>
        )}
      </Card>

      <Tabs
        defaultActiveKey="calendar"
        items={[
          {
            key: "calendar",
            label: "Work Schedule",
            children: <StaffCalendar memberId={memberId} />,
          },
          {
            key: "templates",
            label: "Shift Configuration",
            children: <StaffTemplateEditor memberId={memberId} />,
          },
        ]}
      />
    </div>
  );
}
